// Pick all that apply ('all'): tap every right option, then Lock in sends { optionIds }. Name one ('rarity'): tap one
// right option and it locks ({ optionId }); the fewer players who pick the same right option, the more it scores,
// worked out at the close. Options can run to ten, so they are compact rows with a letter that matches the stage.
// At the reveal every right option is marked; in 'all' the room's share is how many included each option, in
// 'rarity' it is the share of the room that picked it, with this player's points.
import { useMemo, useState } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import type { Option, Reveal } from '@shared/types';
import { Stem } from '@/components/Stem';
import { Button } from '@/components/Button';
import { chem } from '@/clinical/chem';
import { sfx } from '@/audio/sfx';
import { haptic } from '@/lib/haptics';
import type { FormatViewProps } from './types';
import { phaseFor } from './layout';
import { pickOf, useAnswerSubmit, TickGlyph, CrossGlyph } from './parts';
import { creditFor, optionPct, rarityPoints, rightIds, type Credit } from './credit';
import { CreditSplit } from './reveal';
import { useNearing } from './useServerNow';
import { Split, useWide } from './split';

export type ChoiceState = 'idle' | 'picked' | 'locked' | 'dim' | 'correct' | 'missed' | 'wrong';
const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
/** The option's letter: its id when the server named it with one letter, else its place. */
export const letterOf = (o: Option, i: number) => (/^[A-Z]$/i.test(o.id) ? o.id.toUpperCase() : LETTERS[i % 26]);

export function MultiView(props: FormatViewProps) {
  return props.question.multi?.mode === 'rarity' ? <RarityView {...props} /> : <AllView {...props} />;
}

function AllView(props: FormatViewProps) {
  const { question: q, myAnswer, reveal, display = 'phones' } = props;
  const mine = Array.isArray(myAnswer?.optionIds) && myAnswer.optionIds.length ? myAnswer.optionIds : undefined;
  const phase = phaseFor(props.phase, Boolean(mine), reveal);
  const { submit, live } = useAnswerSubmit({ ...props, phase });
  const [picked, setPicked] = useState<string[]>([]);
  const screen = display === 'screen';
  const revealed = phase === 'revealed' && reveal ? reveal : undefined;
  const sel = mine ?? picked;
  // Picks that are not locked in score nothing: in the last seconds the bar says so.
  const late = useNearing(q.deadlineAt, 5000, live && picked.length > 0);

  const toggle = (id: string) => {
    if (!live) return;
    const on = picked.includes(id);
    setPicked(on ? picked.filter((x) => x !== id) : [...picked, id]);
    if (!on) sfx.play('pop');
    haptic('tap');
  };

  const wide = useWide(display);
  return (
    <div className="fx-format" data-format="multi" data-mode="all" data-phase={phase} data-display={display} data-layout={wide ? 'split' : undefined}>
      <Split wide={wide} left={<>
      {!screen && <Stem text={q.stem} />}
      {screen && <p className="sr-only">The question is on the big screen. Answer here.</p>}
      </>} right={revealed ? (
        <MultiResult options={q.options} reveal={revealed} mode="all" mine={mine} credit={mine ? creditFor(q, revealed, { optionIds: mine }) : undefined} pctOf={(id) => optionPct(q, revealed, id)} compact={screen} />
      ) : (
        <>
          <ul className="fx-choices" data-compact={screen ? '' : undefined} aria-label="Pick every right answer">
            {q.options.map((o, i) => {
              const on = sel.includes(o.id);
              const state: ChoiceState = mine ? (on ? 'locked' : 'dim') : on ? 'picked' : 'idle';
              return (
                <li key={o.id}>
                  <button type="button" className="fx-choice" data-kind="check" data-option={o.id} data-state={state} aria-pressed={on} disabled={!live} onClick={() => toggle(o.id)}>
                    <span className="fx-choice-box" aria-hidden="true">{on && <TickGlyph size={16} stroke={3.6} />}</span>
                    <span className="fx-choice-text">{chem(o.text)}</span>
                    {screen && <span className="fx-choice-letter" aria-hidden="true">{letterOf(o, i)}</span>}
                  </button>
                </li>
              );
            })}
          </ul>
          {!mine && (
            <div className="fx-dock" data-docked={wide ? undefined : ''}>
              <p className="fx-dock-note" data-late={late ? '' : undefined}>{late ? 'Lock in before the buzzer.' : 'Every right answer counts. Every wrong one costs.'}</p>
              <Button variant="primary" size="lg" disabled={!live || !picked.length} onClick={() => submit({ optionIds: picked })} className={late ? 'fx-go-late' : ''}>
                {picked.length ? `Lock in ${picked.length === 1 ? 'one answer' : `${picked.length} answers`}` : 'Lock in'}
              </Button>
            </div>
          )}
        </>
      )} />
    </div>
  );
}

function RarityView(props: FormatViewProps) {
  const { question: q, reveal, display = 'phones' } = props;
  const mine = pickOf(props);
  const phase = phaseFor(props.phase, Boolean(mine), reveal);
  const { submit, live } = useAnswerSubmit({ ...props, phase });
  const screen = display === 'screen';
  const revealed = phase === 'revealed' && reveal ? reveal : undefined;

  const wide = useWide(display);
  return (
    <div className="fx-format" data-format="multi" data-mode="rarity" data-phase={phase} data-display={display} data-layout={wide ? 'split' : undefined}>
      <Split wide={wide} left={<>
      {!screen && <Stem text={q.stem} />}
      {screen && <p className="sr-only">The question is on the big screen. Answer here.</p>}
      </>} right={revealed ? (
        <MultiResult options={q.options} reveal={revealed} mode="rarity" mine={mine ? [mine] : undefined} pctOf={(id) => optionPct(q, revealed, id)}
          worth={(id) => rarityPoints(q, revealed, id)} compact={screen} />
      ) : (
        <>
          {/* The scoring rule, said here once: the stem asks for one answer and the footer gives the lock-in rule. */}
          {!mine && <p className="fx-rarity-note">The rarer your right answer, the more it scores.</p>}
          <ul className="fx-choices" data-compact={screen ? '' : undefined} aria-label="Pick one right answer">
            {q.options.map((o, i) => {
              const state: ChoiceState = mine ? (o.id === mine ? 'locked' : 'dim') : 'idle';
              return (
                <li key={o.id}>
                  <button type="button" className="fx-choice" data-kind="radio" data-option={o.id} data-state={state} aria-pressed={o.id === mine} disabled={!live}
                    onClick={() => submit({ optionId: o.id })}>
                    <span className="fx-choice-box" aria-hidden="true" />
                    <span className="fx-choice-text">{chem(o.text)}</span>
                    {screen && <span className="fx-choice-letter" aria-hidden="true">{letterOf(o, i)}</span>}
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      )} />
    </div>
  );
}

/**
 * The answer view for multi: every right option marked, this player's picks, the room's shares (in 'rarity', what
 * each right option paid); reusable on the stage without a player.
 */
export function MultiResult({ options, reveal, mode, mine, credit, pctOf, worth, compact = false }: {
  options: readonly Option[]; reveal: Reveal; mode: 'all' | 'rarity'; mine?: readonly string[]; credit?: Credit;
  pctOf: (id: string) => number; worth?: (id: string) => number | undefined; compact?: boolean;
}) {
  const reduce = useReducedMotion() ?? false;
  const right = useMemo(() => rightIds(reveal), [reveal]);
  const hits = mine ? mine.filter((id) => right.includes(id)).length : 0;
  const misses = mine ? mine.length - hits : 0;
  // Pick all: how the picks went, in a line. Name one: the headline and the marks already say it.
  const verdict = mine && mode === 'all' ? `You found ${hits} of ${right.length}${misses ? `, and picked ${misses === 1 ? 'one' : misses} wrong` : ''}.` : undefined;
  return (
    <section className="fx-multi" data-mode={mode} data-compact={compact ? '' : undefined} aria-label="The answers">
      <ul className="fx-choices" data-compact={compact ? '' : undefined} data-revealed="">
        {options.map((o, i) => {
          const isRight = right.includes(o.id);
          const on = Boolean(mine?.includes(o.id));
          const state: ChoiceState = isRight ? (on || !mine || mode === 'rarity' ? 'correct' : 'missed') : on ? 'wrong' : 'dim';
          const share = pctOf(o.id);
          const value = mode === 'rarity' && isRight ? worth?.(o.id) : undefined;
          // What the marks, the tag and the share say, in words, for screen readers.
          const said = ` ${isRight ? 'A right answer.' : 'Not a right answer.'}${on ? ' Your pick.' : state === 'missed' ? ' You missed it.' : ''} ${share}% ${mode === 'rarity' ? 'picked it' : 'included it'}.${value !== undefined ? ` Worth ${value} points.` : ''}`;
          return (
            <motion.li key={o.id} className="fx-choice" data-kind={mode === 'rarity' ? 'radio' : 'check'} data-option={o.id} data-state={state} data-mine={on ? '' : undefined}
              initial={reduce ? { opacity: 0 } : { opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 + i * 0.04, type: 'spring', stiffness: 420, damping: 32 }}>
              <span className="fx-choice-share" aria-hidden="true">
                <motion.i initial={reduce ? false : { scaleX: 0 }} animate={{ scaleX: share / 100 }} transition={{ type: 'spring', stiffness: 120, damping: 20, delay: 0.25 + i * 0.04 }} />
              </span>
              <span className="fx-choice-box" aria-hidden="true">
                {state === 'correct' || state === 'missed' ? <TickGlyph size={16} stroke={3.6} /> : state === 'wrong' ? <CrossGlyph size={16} stroke={3.6} /> : null}
              </span>
              <span className="fx-choice-text">
                {chem(o.text)}
                <span className="sr-only">{said}</span>
                {(on || state === 'missed') && <span className="fx-choice-tag" data-tone={state} aria-hidden="true">{on ? 'You' : 'Missed'}</span>}
              </span>
              <span className="fx-choice-pct" aria-hidden="true">{share}%{value !== undefined && <small>{value.toLocaleString('en-GB')} pts</small>}</span>
            </motion.li>
          );
        })}
      </ul>
      {verdict && <p className="fx-verdict">{verdict}</p>}
      {mode === 'all' && reveal.credit && <CreditSplit credit={reveal.credit} mine={mine ? credit : undefined} />}
    </section>
  );
}
